import React, { useState, useCallback } from 'react';
import ReactFlow, {
  Background,
  Controls,
  useNodesState,
  useEdgesState,
  MarkerType,
} from 'reactflow';
import 'reactflow/dist/style.css';
import './StackObservatory.css';

// ─────────────────────────────────────────────
// DUMMY DATA
// ─────────────────────────────────────────────

const technology_registry = [
  // Server-1
  { id: 't1', name: 'FastAPI', server: 'Server-1', category: 'Backend Framework', current_version: '0.103.1', latest_version: '0.104.0', risk_level: 'Low', status: 'Healthy', criticality: 'HIGH', pricing: 'Free / Open Source' },
  { id: 't2', name: 'Keycloak', server: 'Server-1', category: 'Auth Layer', current_version: '22.0.1', latest_version: '22.0.5', risk_level: 'Medium', status: 'Updates Available', criticality: 'CRITICAL', pricing: 'Free / Open Source' },
  { id: 't3', name: 'Supabase', server: 'Server-1', category: 'Auth Layer', current_version: '2.33.1', latest_version: '2.38.0', risk_level: 'Low', status: 'Healthy', criticality: 'HIGH', pricing: '$25 / month (Pro Plan)' },
  { id: 't4', name: 'Cloud Tasks (GCP)', server: 'Server-1', category: 'Cloud Services', current_version: 'v2', latest_version: 'v2', risk_level: 'Low', status: 'Healthy', criticality: 'HIGH', pricing: 'Pay as you go' },
  { id: 't5', name: 'Stripe', server: 'Server-1', category: 'Payment', current_version: '14.5.0', latest_version: '14.10.0', risk_level: 'Low', status: 'Healthy', criticality: 'HIGH', pricing: 'Per-transaction fee' },
  { id: 't6', name: 'Firestore / Firebase', server: 'Server-1', category: 'Cloud Services', current_version: 'v1', latest_version: 'v1', risk_level: 'Low', status: 'Healthy', criticality: 'CRITICAL', pricing: 'Pay as you go (Blaze Plan)' },
  
  // Server-2
  { id: 't7', name: 'LangChain', server: 'Server-2', category: 'AI Framework', current_version: '0.0.300', latest_version: '0.1.0', risk_level: 'High', status: 'Breaking Changes', criticality: 'CRITICAL', pricing: 'Free / Open Source' },
  { id: 't8', name: 'OpenAI GPT-4o Mini', server: 'Server-2', category: 'AI Provider', current_version: '1.3.5', latest_version: '1.14.0', risk_level: 'Low', status: 'Healthy', criticality: 'CRITICAL', pricing: '$0.15 / 1M input tokens' },
  { id: 't9', name: 'Gemini 2.5 Flash', server: 'Server-2', category: 'AI Provider', current_version: '1.0.0', latest_version: '1.5.0', risk_level: 'Low', status: 'Healthy', criticality: 'HIGH', pricing: '$0.075 / 1M tokens' },
  { id: 't10', name: 'OpenRouter', server: 'Server-2', category: 'AI Provider', current_version: 'v1', latest_version: 'v1', risk_level: 'Medium', status: 'Latency Spike', criticality: 'MEDIUM', pricing: 'Pay as you go (Provider rates)' },
  { id: 't11', name: 'Pinecone', server: 'Server-2', category: 'Vector/RAG', current_version: '2.2.2', latest_version: '3.0.0', risk_level: 'High', status: 'Migration Required', criticality: 'HIGH', pricing: '$70 / month (Standard)' },
  { id: 't12', name: 'Playwright', server: 'Server-2', category: 'Browser Automation', current_version: '1.38.0', latest_version: '1.42.0', risk_level: 'Medium', status: 'Updates Available', criticality: 'HIGH', pricing: 'Free / Open Source' },

  // Client / Infrastructure
  { id: 't13', name: 'React', server: 'Client', category: 'Frontend Library', current_version: '18.3.1', latest_version: '18.3.1', risk_level: 'Low', status: 'Healthy', criticality: 'CRITICAL', pricing: 'Free / Open Source' },
  { id: 't14', name: 'Tailwind CSS', server: 'Client', category: 'Styling', current_version: '3.4.0', latest_version: '3.4.0', risk_level: 'Low', status: 'Healthy', criticality: 'MEDIUM', pricing: 'Free / Open Source' },
  { id: 't15', name: 'Vercel', server: 'Client', category: 'Hosting', current_version: 'latest', latest_version: 'latest', risk_level: 'Low', status: 'Healthy', criticality: 'HIGH', pricing: '$20 / month (Pro Plan)' },
];

const dependency_events = [
  { id: 'e1', technology_id: 't7', event_type: 'Version Intelligence', severity: 'High', title: 'LangChain breaking release', summary: 'LangChain 0.1.0 introduces breaking memory changes.', published_at: '2026-05-10', requires_action: true },
  { id: 'e2', technology_id: 't8', event_type: 'Reliability Intelligence', severity: 'Critical', title: 'OpenAI model deprecated', summary: 'gpt-3.5-turbo-0613 deprecation date approaching.', published_at: '2026-05-12', requires_action: true },
  { id: 'e3', technology_id: 't10', event_type: 'Reliability Intelligence', severity: 'Medium', title: 'OpenRouter latency increased 32%', summary: 'Observed latency spikes in the last 48 hours.', published_at: '2026-05-13', requires_action: false },
  { id: 'e4', technology_id: 't5', event_type: 'Policy Intelligence', severity: 'Low', title: 'Stripe webhook schema changed', summary: 'Minor additions to the event payload.', published_at: '2026-05-01', requires_action: true },
];

// Graph Nodes & Edges setup
const initialNodes = [
  { id: 'chat-tool', data: { label: 'Chat Tool (Client)' }, position: { x: 250, y: 50 }, style: { background: '#f8fafc', border: '2px solid #cbd5e1', borderRadius: '8px', padding: '10px', fontWeight: 'bold' } },
  { id: 'server-1', data: { label: 'Server-1 (API Gateway)' }, position: { x: 100, y: 150 }, style: { background: '#e0e7ff', border: '2px solid #4f46e5', borderRadius: '8px', padding: '10px' } },
  { id: 'server-2', data: { label: 'Server-2 (AI Engine)' }, position: { x: 400, y: 150 }, style: { background: '#fae8ff', border: '2px solid #c026d3', borderRadius: '8px', padding: '10px' } },
  
  // Server-1 dependencies
  { id: 'fastapi', data: { label: 'FastAPI', techId: 't1' }, position: { x: 0, y: 250 }, style: { background: '#fff', border: '1px solid #e2e8f0', borderRadius: '4px', padding: '8px', fontSize: '12px' } },
  { id: 'supabase', data: { label: 'Supabase', techId: 't3' }, position: { x: 100, y: 250 }, style: { background: '#fff', border: '1px solid #e2e8f0', borderRadius: '4px', padding: '8px', fontSize: '12px' } },
  { id: 'keycloak', data: { label: 'Keycloak', techId: 't2' }, position: { x: 200, y: 250 }, style: { background: '#fff', border: '1px solid #e2e8f0', borderRadius: '4px', padding: '8px', fontSize: '12px' } },

  // Server-2 dependencies
  { id: 'langchain', data: { label: 'LangChain', techId: 't7' }, position: { x: 300, y: 250 }, style: { background: '#fee2e2', border: '1px solid #ef4444', borderRadius: '4px', padding: '8px', fontSize: '12px' } },
  { id: 'openai', data: { label: 'OpenAI', techId: 't8' }, position: { x: 400, y: 250 }, style: { background: '#fee2e2', border: '1px solid #ef4444', borderRadius: '4px', padding: '8px', fontSize: '12px' } },
  { id: 'gemini', data: { label: 'Gemini', techId: 't9' }, position: { x: 500, y: 250 }, style: { background: '#fff', border: '1px solid #e2e8f0', borderRadius: '4px', padding: '8px', fontSize: '12px' } },
  { id: 'pinecone', data: { label: 'Pinecone', techId: 't11' }, position: { x: 600, y: 250 }, style: { background: '#fef3c7', border: '1px solid #f59e0b', borderRadius: '4px', padding: '8px', fontSize: '12px' } },
];

const initialEdges = [
  { id: 'e-chat-s1', source: 'chat-tool', target: 'server-1', animated: true, markerEnd: { type: MarkerType.ArrowClosed } },
  { id: 'e-chat-s2', source: 'chat-tool', target: 'server-2', animated: true, markerEnd: { type: MarkerType.ArrowClosed } },
  
  { id: 'e-s1-f', source: 'server-1', target: 'fastapi' },
  { id: 'e-s1-s', source: 'server-1', target: 'supabase' },
  { id: 'e-s1-k', source: 'server-1', target: 'keycloak' },

  { id: 'e-s2-l', source: 'server-2', target: 'langchain', style: { stroke: '#ef4444', strokeWidth: 2 } },
  { id: 'e-s2-o', source: 'server-2', target: 'openai', style: { stroke: '#ef4444', strokeWidth: 2 } },
  { id: 'e-s2-g', source: 'server-2', target: 'gemini' },
  { id: 'e-s2-p', source: 'server-2', target: 'pinecone' },
];

export default function StackObservatory() {
  const [activeTab, setActiveTab] = useState('overview');
  const [nodes, setNodes, onNodesChange] = useNodesState(initialNodes);
  const [edges, setEdges, onEdgesChange] = useEdgesState(initialEdges);
  const [selectedNodeData, setSelectedNodeData] = useState(null);

  const getRiskBadgeClass = (risk) => {
    switch (risk.toLowerCase()) {
      case 'critical': return 'badge-critical';
      case 'high': return 'badge-high';
      case 'medium': return 'badge-medium';
      case 'low': return 'badge-low';
      default: return 'badge-healthy';
    }
  };

  const renderTable = (serverFilter) => {
    const techList = technology_registry.filter(t => t.server === serverFilter);
    return (
      <div className="obs-table-wrap">
        <table className="obs-table">
          <thead>
            <tr>
              <th>Technology</th>
              <th>Category</th>
              <th>Version (Cur / Latest)</th>
              <th>Status</th>
              <th>Risk Level</th>
            </tr>
          </thead>
          <tbody>
            {techList.map(tech => (
              <tr key={tech.id}>
                <td>
                  <div className="tech-name">{tech.name}</div>
                  <div className="tech-sub">{tech.criticality} CRITICALITY</div>
                </td>
                <td>{tech.category}</td>
                <td style={{ fontFamily: 'monospace', fontSize: '0.7rem' }}>
                  {tech.current_version} / {tech.latest_version}
                </td>
                <td>{tech.status}</td>
                <td>
                  <span className={`obs-badge ${getRiskBadgeClass(tech.risk_level)}`}>
                    {tech.risk_level}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  const onNodeClick = useCallback((event, node) => {
    if (node.data && node.data.techId) {
      const techInfo = technology_registry.find(t => t.id === node.data.techId);
      const techEvents = dependency_events.filter(e => e.technology_id === node.data.techId);
      setSelectedNodeData({ ...techInfo, events: techEvents });
    } else {
      setSelectedNodeData({ name: node.data.label, description: "Core Architecture Component" });
    }
  }, []);

  return (
    <div className="obs-container">
      {/* ── HEADER ── */}
      <div className="obs-header">
        <div className="obs-header-left">
          <h1>🔭 Stack Observatory</h1>
          <p>Operational Dependency Intelligence Platform</p>
        </div>
      </div>

      {/* ── TABS ── */}
      <div className="obs-tabs">
        {[
          { id: 'overview', label: 'Overview' },
          { id: 'server1', label: 'Server-1 (Gateway)' },
          { id: 'server2', label: 'Server-2 (AI Engine)' },
          { id: 'graph', label: 'Dependency Graph View' }
        ].map(tab => (
          <button
            key={tab.id}
            className={`obs-tab-btn ${activeTab === tab.id ? 'active' : ''}`}
            onClick={() => setActiveTab(tab.id)}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* ── CONTENT ── */}
      {activeTab === 'overview' && (
        <>
          <div className="obs-cards-row">
            <div className="obs-stat-card">
              <div className="obs-stat-icon icon-critical">🚨</div>
              <div className="obs-stat-info"><h3>2</h3><p>Critical Issues</p></div>
            </div>
            <div className="obs-stat-card">
              <div className="obs-stat-icon icon-risk">⚠️</div>
              <div className="obs-stat-info"><h3>3</h3><p>Dependencies At Risk</p></div>
            </div>
            <div className="obs-stat-card">
              <div className="obs-stat-icon icon-outdated">📦</div>
              <div className="obs-stat-info"><h3>5</h3><p>Outdated Libraries</p></div>
            </div>
            <div className="obs-stat-card">
              <div className="obs-stat-icon icon-cost">💳</div>
              <div className="obs-stat-info"><h3>8</h3><p>Active Subscriptions</p></div>
            </div>
          </div>

          <div className="obs-section">
            <div className="obs-section-header">
              <div>
                <h2>Recent Incidents & Events</h2>
                <p>Latest alerts across all intelligence categories</p>
              </div>
            </div>
            <div className="obs-table-wrap">
              <table className="obs-table">
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Technology</th>
                    <th>Category</th>
                    <th>Summary</th>
                    <th>Severity</th>
                  </tr>
                </thead>
                <tbody>
                  {dependency_events.map(ev => {
                    const tech = technology_registry.find(t => t.id === ev.technology_id);
                    return (
                      <tr key={ev.id}>
                        <td style={{ color: '#64748b' }}>{ev.published_at}</td>
                        <td className="tech-name">{tech?.name}</td>
                        <td>{ev.event_type}</td>
                        <td>
                          <div style={{ fontWeight: 600 }}>{ev.title}</div>
                          <div className="tech-sub">{ev.summary}</div>
                        </td>
                        <td>
                          <span className={`obs-badge ${getRiskBadgeClass(ev.severity)}`}>
                            {ev.severity}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          <div className="obs-section">
            <div className="obs-section-header">
              <div>
                <h2>Active Subscriptions & Pricing</h2>
                <p>Tracked SaaS, Cloud, and AI Model costs</p>
              </div>
            </div>
            <div className="obs-table-wrap">
              <table className="obs-table">
                <thead>
                  <tr>
                    <th>Service / Model</th>
                    <th>Category</th>
                    <th>Pricing Model</th>
                  </tr>
                </thead>
                <tbody>
                  {technology_registry.filter(t => t.pricing && t.pricing !== 'Free / Open Source').map(tech => (
                    <tr key={`price-${tech.id}`}>
                      <td className="tech-name">{tech.name}</td>
                      <td>{tech.category}</td>
                      <td style={{ fontWeight: 600, color: '#15803d' }}>{tech.pricing}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      {activeTab === 'server1' && (
        <div className="obs-section">
          <div className="obs-section-header">
            <div>
              <h2>Server-1 Infrastructure</h2>
              <p>API Gateway, Auth, Queues, Storage</p>
            </div>
          </div>
          {renderTable('Server-1')}
        </div>
      )}

      {activeTab === 'server2' && (
        <div className="obs-section">
          <div className="obs-section-header">
            <div>
              <h2>Server-2 AI Engine</h2>
              <p>Orchestration, Models, RAG, Browsers</p>
            </div>
          </div>
          {renderTable('Server-2')}
        </div>
      )}

      {activeTab === 'graph' && (
        <div className="obs-section" style={{ position: 'relative' }}>
          <div className="obs-section-header">
            <div>
              <h2>Dependency Graph Visualization</h2>
              <p>Interactive mapping of the platform architecture</p>
            </div>
          </div>
          <div className="react-flow-container">
            <ReactFlow
              nodes={nodes}
              edges={edges}
              onNodesChange={onNodesChange}
              onEdgesChange={onEdgesChange}
              onNodeClick={onNodeClick}
              fitView
              attributionPosition="bottom-left"
            >
              <Background color="#ccc" gap={16} />
              <Controls />
            </ReactFlow>

            {selectedNodeData && (
              <div className="graph-side-panel">
                <h3>{selectedNodeData.name}</h3>
                <p>{selectedNodeData.description || `${selectedNodeData.category} - ${selectedNodeData.server}`}</p>
                
                {selectedNodeData.status && (
                  <div className="panel-detail">
                    <div className="panel-detail-item">
                      <span className="panel-label">Status:</span>
                      <span className="panel-value">{selectedNodeData.status}</span>
                    </div>
                    <div className="panel-detail-item">
                      <span className="panel-label">Version:</span>
                      <span className="panel-value">{selectedNodeData.current_version}</span>
                    </div>
                    <div className="panel-detail-item">
                      <span className="panel-label">Latest:</span>
                      <span className="panel-value">{selectedNodeData.latest_version}</span>
                    </div>
                  </div>
                )}

                {selectedNodeData.events && selectedNodeData.events.length > 0 && (
                  <div className="panel-detail">
                    <span className="panel-label" style={{ display: 'block', marginBottom: '5px' }}>Recent Events:</span>
                    {selectedNodeData.events.map(ev => (
                      <div key={ev.id} style={{ fontSize: '0.65rem', marginBottom: '4px', color: '#b91c1c' }}>
                        • {ev.title}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
